/**
 * Continuous per-theme layers, built once per theme and driven every frame through smoothed
 * params (no per-frame node allocation):
 *
 *  - Wind / rush — THE speed feel. Pink band-pass "rush" + high "air" hiss + low buffeting roar,
 *    all opening up with speed; more roar + whistle airborne; panned slightly with steering.
 *  - Ground contact — sky: grass swish (band-passed noise, random + rhythmic AM tied to speed);
 *    dunes: sand hiss + granular crackle. Holding adds a deeper, heavier slide.
 *  - Chaser — brown-noise rumble with tremolo + a saturated sub drone; sky rolls like thunder,
 *    dunes adds a roaring sand wall with a gritty high layer.
 *  - Bed — sky: cloud-sea wash + distant waterfall, birds; dunes: hot low air, sand gusts, singing dunes.
 */
import { FadeRoute } from './bus';
import { birdCall, sandGust, singingDune, thunder } from './instruments/nature';
import { type Kit, type Synth, link } from './instruments/synth';
import type { ThemeDef } from './themes';
import type { AudioFrame } from './types';
import { Smooth, approach, clamp, clamp01, disconnect, lerp, makePanner, stopSource } from './util';
import type { Route, VoicePool } from './voice';

/** Per-frame inputs shared by every layer (one mutable object). */
interface AmbIn {
  now: number;
  dt: number;
  playing: boolean;
  idle: boolean;
  crashed: boolean;
  /** Effective speed 0..1 (falls to 0 when not running). */
  sn: number;
  /** 1 when airborne while running. */
  air: number;
  /** 1 when grounded while running. */
  ground: number;
  holding: number;
  steer: number;
  prox: number;
  /** Slow random gust 0..1. */
  gust: number;
  /** Speed-response time constant (slower when dying down after a crash). */
  tau: number;
}

/** Owns persistent nodes for a layer set; everything is started at construction and stopped on dispose. */
class Rig {
  private readonly nodes: AudioNode[] = [];
  private readonly srcs: AudioScheduledSourceNode[] = [];

  constructor(
    readonly ctx: BaseAudioContext,
    readonly kit: Kit,
    private readonly t: number,
  ) {}

  loop(buf: AudioBuffer, rate = 1): AudioBufferSourceNode {
    const s = this.ctx.createBufferSource();
    s.buffer = buf;
    s.loop = true;
    s.playbackRate.value = rate;
    s.start(this.t, this.kit.rng.next() * buf.duration * 0.95);
    this.nodes.push(s);
    this.srcs.push(s);
    return s;
  }

  osc(shape: OscillatorType | PeriodicWave, f: number): OscillatorNode {
    const o = this.ctx.createOscillator();
    // (PeriodicWave is a structurally empty interface, so narrow explicitly.)
    if (typeof shape === 'string') o.type = shape as OscillatorType;
    else o.setPeriodicWave(shape);
    o.frequency.value = f;
    o.start(this.t);
    this.nodes.push(o);
    this.srcs.push(o);
    return o;
  }

  gain(v = 0): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = v;
    this.nodes.push(g);
    return g;
  }

  filter(type: BiquadFilterType, f: number, q = 0.7071): BiquadFilterNode {
    const b = this.ctx.createBiquadFilter();
    b.type = type;
    b.frequency.value = f;
    b.Q.value = q;
    this.nodes.push(b);
    return b;
  }

  panner(): StereoPannerNode | null {
    const p = makePanner(this.ctx);
    if (p) this.nodes.push(p);
    return p;
  }

  shaper(drive: number): WaveShaperNode {
    const w = this.ctx.createWaveShaper();
    const n = 1024;
    const curve = new Float32Array(n);
    const norm = Math.tanh(drive);
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(drive * x) / norm;
    }
    w.curve = curve;
    this.nodes.push(w);
    return w;
  }

  dispose(): void {
    for (const s of this.srcs) stopSource(s);
    for (const n of this.nodes) disconnect(n);
    this.srcs.length = 0;
    this.nodes.length = 0;
  }
}

interface Layer {
  update(i: AmbIn): void;
}

// ───────────────────────────── wind ─────────────────────────────

interface WindMix {
  readonly breeze: number;
  readonly rushMax: number;
  readonly rushLo: number;
  readonly rushHi: number;
  readonly hissMax: number;
  readonly hissLo: number;
  readonly hissHi: number;
  readonly roarMax: number;
  readonly roarLo: number;
  readonly roarHi: number;
  readonly pan: number;
  readonly send: number;
}

const SKY_WIND: WindMix = {
  breeze: 0.24,
  rushMax: 0.75,
  rushLo: 380,
  rushHi: 2700,
  hissMax: 0.3,
  hissLo: 3800,
  hissHi: 7400,
  roarMax: 0.45,
  roarLo: 140,
  roarHi: 650,
  pan: 0.3,
  send: 0.12,
};

const DUNES_WIND: WindMix = {
  breeze: 0.2,
  rushMax: 0.72,
  rushLo: 300,
  rushHi: 2100,
  hissMax: 0.32,
  hissLo: 3200,
  hissHi: 6200,
  roarMax: 0.5,
  roarLo: 120,
  roarHi: 540,
  pan: 0.3,
  send: 0.06,
};

class Wind implements Layer {
  private readonly rush: Smooth;
  private readonly rushF: Smooth;
  private readonly rushQ: Smooth;
  private readonly rushRate: Smooth;
  private readonly hiss: Smooth;
  private readonly hissF: Smooth;
  private readonly roar: Smooth;
  private readonly roarF: Smooth;
  private readonly flutter: Smooth;
  private readonly pan: Smooth | null;

  constructor(
    rig: Rig,
    out: Route,
    private readonly m: WindMix,
  ) {
    const noise = rig.kit.noise;
    const rushSrc = rig.loop(noise.pink);
    const rushBp = rig.filter('bandpass', m.rushLo, 0.6);
    const rushG = rig.gain(0);
    const hissSrc = rig.loop(noise.white);
    const hissBp = rig.filter('bandpass', m.hissLo, 1.3);
    const hissG = rig.gain(0);
    const p = rig.panner();
    const bus = rig.gain(1);
    link(rushSrc, rushBp, rushG, bus);
    link(hissSrc, hissBp, hissG, bus);
    if (p) link(bus, p, out.dry);
    else bus.connect(out.dry);
    const send = rig.gain(m.send);
    link(p ?? bus, send, out.wet);

    // Low buffeting roar, amplitude-modulated by slowed brown noise.
    const roarSrc = rig.loop(noise.brown);
    const roarLp = rig.filter('lowpass', m.roarLo, 0.7);
    const roarG = rig.gain(0);
    link(roarSrc, roarLp, roarG, out.dry);
    const flutSrc = rig.loop(noise.brown, 1.3);
    const flutLp = rig.filter('lowpass', 14, 0.5);
    const flutG = rig.gain(0);
    link(flutSrc, flutLp, flutG);
    flutG.connect(roarG.gain);

    this.rush = new Smooth(rushG.gain, 2e-4);
    this.rushF = new Smooth(rushBp.frequency, 2);
    this.rushQ = new Smooth(rushBp.Q, 0.01);
    this.rushRate = new Smooth(rushSrc.playbackRate, 0.002);
    this.hiss = new Smooth(hissG.gain, 2e-4);
    this.hissF = new Smooth(hissBp.frequency, 5);
    this.roar = new Smooth(roarG.gain, 2e-4);
    this.roarF = new Smooth(roarLp.frequency, 2);
    this.flutter = new Smooth(flutG.gain, 2e-4);
    this.pan = p ? new Smooth(p.pan, 0.005) : null;
  }

  update(i: AmbIn): void {
    const m = this.m;
    const { now, tau, sn, air, gust } = i;
    const s2 = sn * sn;
    const gustAmt = 0.65 + 0.7 * gust;
    this.rush.to(m.breeze * gustAmt + m.rushMax * Math.pow(sn, 1.35) * (1 + 0.15 * air), now, tau);
    this.rushF.to(m.rushLo + (m.rushHi - m.rushLo) * Math.pow(sn, 1.1) + 220 * gust, now, tau);
    this.rushQ.to(0.55 + 0.6 * sn, now, tau);
    this.rushRate.to(0.92 + 0.22 * sn, now, tau);
    this.hiss.to(m.hissMax * (0.05 * gust + s2 * (0.7 + 0.5 * air)), now, tau);
    this.hissF.to(lerp(m.hissLo, m.hissHi, sn), now, tau);
    const roar = m.roarMax * (0.05 + s2 * (0.75 + 0.45 * air));
    this.roar.to(roar, now, tau);
    this.roarF.to(lerp(m.roarLo, m.roarHi, sn), now, tau);
    // Ground buffeting flutters; airborne flow is smoother.
    this.flutter.to(roar * 4 * sn * (1 - 0.5 * air), now, tau);
    this.pan?.to(clamp(i.steer * m.pan, -1, 1), now, 0.12);
  }
}

// ───────────────────────────── ground ─────────────────────────────

class GrassGround implements Layer {
  private readonly swish: Smooth;
  private readonly swishF: Smooth;
  private readonly modRate: Smooth;
  private readonly lfoF: Smooth;
  private readonly slide: Smooth;
  private readonly slideF: Smooth;

  constructor(rig: Rig, out: Route) {
    const noise = rig.kit.noise;
    const src = rig.loop(noise.white);
    const bp = rig.filter('bandpass', 2600, 0.8);
    const am = rig.gain(0.5);
    const g = rig.gain(0);
    link(src, bp, am, g, out.dry);
    // Random flutter (blades brushing past) + a rhythmic swish, both faster with speed.
    const mod = rig.loop(noise.brown, 1);
    const modLp = rig.filter('lowpass', 45, 0.5);
    const modAmt = rig.gain(3.5);
    link(mod, modLp, modAmt);
    modAmt.connect(am.gain);
    const lfo = rig.osc('sine', 3);
    const lfoAmt = rig.gain(0.22);
    link(lfo, lfoAmt);
    lfoAmt.connect(am.gain);

    const slideSrc = rig.loop(noise.pink);
    const slideBp = rig.filter('bandpass', 420, 1.1);
    const slideG = rig.gain(0);
    link(slideSrc, slideBp, slideG, out.dry);

    this.swish = new Smooth(g.gain, 2e-4);
    this.swishF = new Smooth(bp.frequency, 5);
    this.modRate = new Smooth(mod.playbackRate, 0.01);
    this.lfoF = new Smooth(lfo.frequency, 0.05);
    this.slide = new Smooth(slideG.gain, 2e-4);
    this.slideF = new Smooth(slideBp.frequency, 2);
  }

  update(i: AmbIn): void {
    const { now, sn, ground, holding } = i;
    const fast = ground ? 0.03 : 0.025;
    this.swish.to(ground * (0.08 + 0.6 * sn) * (1 - 0.35 * holding), now, fast);
    this.swishF.to(1800 + 2600 * sn, now, 0.1);
    this.modRate.to(0.5 + 2.8 * sn, now, 0.1);
    this.lfoF.to(2.2 + 7 * sn, now, 0.1);
    this.slide.to(ground * holding * (0.12 + 0.5 * sn), now, 0.05);
    this.slideF.to(320 + 480 * sn, now, 0.1);
  }
}

class SandGround implements Layer {
  private readonly hiss: Smooth;
  private readonly hissF: Smooth;
  private readonly crack: Smooth;
  private readonly crackRate: Smooth;
  private readonly slide: Smooth;
  private readonly slideGrit: Smooth;
  private readonly slideF: Smooth;

  constructor(rig: Rig, out: Route) {
    const noise = rig.kit.noise;
    const hissSrc = rig.loop(noise.white);
    const hp = rig.filter('highpass', 3800, 0.6);
    const hissG = rig.gain(0);
    link(hissSrc, hp, hissG, out.dry);

    const crackSrc = rig.loop(noise.crackle, 1);
    const crackBp = rig.filter('bandpass', 2600, 0.6);
    const crackG = rig.gain(0);
    link(crackSrc, crackBp, crackG, out.dry);
    // The same grains, low and heavy, for the held slide's scrape.
    const gritBp = rig.filter('bandpass', 900, 0.8);
    const gritG = rig.gain(0);
    link(crackSrc, gritBp, gritG, out.dry);

    const slideSrc = rig.loop(noise.brown);
    const slideBp = rig.filter('bandpass', 280, 0.9);
    const slideG = rig.gain(0);
    link(slideSrc, slideBp, slideG, out.dry);

    this.hiss = new Smooth(hissG.gain, 2e-4);
    this.hissF = new Smooth(hp.frequency, 5);
    this.crack = new Smooth(crackG.gain, 2e-4);
    this.crackRate = new Smooth(crackSrc.playbackRate, 0.01);
    this.slide = new Smooth(slideG.gain, 2e-4);
    this.slideGrit = new Smooth(gritG.gain, 2e-4);
    this.slideF = new Smooth(slideBp.frequency, 2);
  }

  update(i: AmbIn): void {
    const { now, sn, ground, holding } = i;
    const fast = 0.03;
    this.hiss.to(ground * (0.05 + 0.35 * sn), now, fast);
    this.hissF.to(3000 + 2500 * sn, now, 0.1);
    this.crack.to(ground * (0.1 + 0.55 * sn), now, fast);
    this.crackRate.to(0.45 + 1.6 * sn, now, 0.1);
    this.slide.to(ground * holding * (0.2 + 0.6 * sn), now, 0.05);
    this.slideGrit.to(ground * holding * (0.15 + 0.45 * sn), now, 0.05);
    this.slideF.to(240 + 360 * sn, now, 0.1);
  }
}

// ───────────────────────────── chaser ─────────────────────────────

class Chaser implements Layer {
  private readonly rumble: Smooth;
  private readonly rumbleF: Smooth;
  private readonly trem: Smooth;
  private readonly tremF: Smooth;
  private readonly roll: Smooth | null = null;
  private readonly sub: Smooth;
  private readonly grit: Smooth | null = null;
  private readonly roar: Smooth | null = null;
  private readonly roarF: Smooth | null = null;

  constructor(rig: Rig, out: Route, sky: boolean) {
    const noise = rig.kit.noise;
    const src = rig.loop(noise.brown, 0.9);
    const lp = rig.filter('lowpass', 120, 0.7);
    const g = rig.gain(0);
    link(src, lp, g, out.dry);
    const send = rig.gain(sky ? 0.25 : 0.1);
    link(g, send, out.wet);

    const lfo = rig.osc('sine', 0.3);
    const tremG = rig.gain(0);
    link(lfo, tremG);
    tremG.connect(g.gain);

    if (sky) {
      // Rolling thunder: very slow brown noise as an amplitude modulator.
      const roll = rig.loop(noise.brown, 0.2);
      const rollLp = rig.filter('lowpass', 2.5, 0.5);
      const rollG = rig.gain(0);
      link(roll, rollLp, rollG);
      rollG.connect(g.gain);
      this.roll = new Smooth(rollG.gain, 2e-4);
    }

    // Sub drone: beating sines (sky) / an ominous minor second (dunes), saturated so small speakers hear it.
    const a = rig.osc('sine', 73.42);
    const b = rig.osc('sine', sky ? 73.42 * 1.0075 : 77.78);
    const pre = rig.gain(0.7);
    const shaper = rig.shaper(2.2);
    const subG = rig.gain(0);
    a.connect(pre);
    b.connect(pre);
    link(pre, shaper, subG, out.dry);

    if (!sky) {
      const gritSrc = rig.loop(noise.crackle, 1.7);
      const hp = rig.filter('highpass', 3300, 0.6);
      const gritG = rig.gain(0);
      link(gritSrc, hp, gritG, out.dry);
      const roarSrc = rig.loop(noise.pink);
      const bp = rig.filter('bandpass', 500, 0.7);
      const roarG = rig.gain(0);
      link(roarSrc, bp, roarG, out.dry);
      this.grit = new Smooth(gritG.gain, 2e-4);
      this.roar = new Smooth(roarG.gain, 2e-4);
      this.roarF = new Smooth(bp.frequency, 2);
    }

    this.rumble = new Smooth(g.gain, 2e-4);
    this.rumbleF = new Smooth(lp.frequency, 2);
    this.trem = new Smooth(tremG.gain, 2e-4);
    this.tremF = new Smooth(lfo.frequency, 0.01);
    this.sub = new Smooth(subG.gain, 2e-4);
  }

  update(i: AmbIn): void {
    const { now, prox: p } = i;
    const tau = 0.25;
    const rumble = 0.45 * Math.pow(p, 1.5);
    this.rumble.to(rumble, now, tau);
    this.rumbleF.to(90 + 380 * p, now, tau);
    this.trem.to(rumble * 0.35, now, tau);
    this.tremF.to(0.25 + 0.6 * p, now, 0.5);
    this.roll?.to(rumble * 7, now, tau);
    this.sub.to(0.1 * p * p, now, tau);
    this.grit?.to(0.5 * p * p, now, tau);
    this.roar?.to(0.5 * Math.pow(p, 1.6), now, tau);
    this.roarF?.to(450 + 650 * p, now, tau);
  }
}

// ───────────────────────────── bed ─────────────────────────────

class SkyBed implements Layer {
  private readonly wash: Smooth;
  private readonly falls: Smooth;

  constructor(rig: Rig, out: Route) {
    const noise = rig.kit.noise;
    const washSrc = rig.loop(noise.pink, 0.8);
    const lp = rig.filter('lowpass', 750, 0.5);
    const washG = rig.gain(0);
    link(washSrc, lp, washG, out.dry);
    const wSend = rig.gain(0.3);
    link(washG, wSend, out.wet);
    const fallsSrc = rig.loop(noise.white);
    const bp = rig.filter('bandpass', 1500, 0.5);
    const fallsG = rig.gain(0);
    link(fallsSrc, bp, fallsG, out.dry);
    this.wash = new Smooth(washG.gain, 2e-4);
    this.falls = new Smooth(fallsG.gain, 2e-4);
  }

  update(i: AmbIn): void {
    this.wash.to((0.1 + 0.06 * i.gust) * (i.playing ? 0.7 : 1), i.now, 0.8);
    this.falls.to(i.playing ? 0.05 : 0.11, i.now, 1);
  }
}

class DunesBed implements Layer {
  private readonly heat: Smooth;

  constructor(rig: Rig, out: Route) {
    const src = rig.loop(rig.kit.noise.brown, 0.85);
    const lp = rig.filter('lowpass', 200, 0.6);
    const g = rig.gain(0);
    link(src, lp, g, out.dry);
    this.heat = new Smooth(g.gain, 2e-4);
  }

  update(i: AmbIn): void {
    this.heat.to(0.16 * (0.75 + 0.5 * i.gust) * (i.playing ? 0.7 : 1), i.now, 0.8);
  }
}

// ───────────────────────────── ambience ─────────────────────────────

export class Ambience {
  readonly route: FadeRoute;
  private readonly rig: Rig;
  private readonly layers: Layer[];
  private readonly synth: Synth;
  private readonly sky: boolean;
  private readonly inp: AmbIn = {
    now: 0,
    dt: 0,
    playing: false,
    idle: true,
    crashed: false,
    sn: 0,
    air: 0,
    ground: 0,
    holding: 0,
    steer: 0,
    prox: 0,
    gust: 0.5,
    tau: 0.1,
  };
  private gustTarget = 0.5;
  private gustTimer = 0;
  private critterT: number;
  private stormT = 3;
  private rareT: number;

  constructor(kit: Kit, pool: VoicePool, dest: Route, theme: ThemeDef, now: number, fadeIn: number) {
    const ctx = kit.ctx;
    this.sky = theme.id !== 'dunes';
    this.route = new FadeRoute(ctx, dest, 0);
    this.rig = new Rig(ctx, kit, now);
    this.synth = { kit, pool, route: this.route };
    this.layers = [
      new Wind(this.rig, this.route, this.sky ? SKY_WIND : DUNES_WIND),
      this.sky ? new GrassGround(this.rig, this.route) : new SandGround(this.rig, this.route),
      new Chaser(this.rig, this.route, this.sky),
      this.sky ? new SkyBed(this.rig, this.route) : new DunesBed(this.rig, this.route),
    ];
    this.critterT = kit.rng.range(2, 5);
    this.rareT = kit.rng.range(15, 30);
    this.route.fade(1, now, Math.max(0.3, fadeIn));
  }

  update(frame: AudioFrame, dt: number, now: number): void {
    const i = this.inp;
    const st = frame.state;
    if (st === 'paused') return; // hold everything; the bus ducks it
    const rng = this.synth.kit.rng;
    const playing = st === 'playing';
    i.now = now;
    i.dt = dt;
    i.playing = playing;
    i.idle = st === 'idle';
    i.crashed = st === 'crashed';
    i.sn = playing ? clamp01(frame.speedNorm) : 0;
    i.air = playing && !frame.grounded ? 1 : 0;
    i.ground = playing && frame.grounded ? 1 : 0;
    i.holding = frame.holding ? 1 : 0;
    i.steer = playing ? clamp(frame.steer || 0, -1, 1) : 0;
    i.prox = playing ? clamp01(frame.chaserProximity) : 0;
    i.tau = i.crashed ? 0.35 : 0.1;

    this.gustTimer -= dt;
    if (this.gustTimer <= 0) {
      this.gustTarget = rng.next();
      this.gustTimer = rng.range(1.5, 4.5);
    }
    i.gust = approach(i.gust, this.gustTarget, dt, 1.2);

    for (const l of this.layers) l.update(i);
    this.oneShots(i, dt);
  }

  /** Sparse ambient events (allocated at event rate, never per frame). */
  private oneShots(i: AmbIn, dt: number): void {
    const rng = this.synth.kit.rng;
    const t = i.now + 0.02;
    this.critterT -= dt;
    this.rareT -= dt;
    if (this.sky) {
      if (this.critterT <= 0) {
        if (i.idle || (i.playing && i.sn < 0.75)) birdCall(this.synth, t, i.playing ? 0.055 : 0.08, rng.bi() * 0.7);
        this.critterT = i.playing ? rng.range(9, 20) : rng.range(5, 12);
      }
      if (i.playing && i.prox > 0.3) {
        this.stormT -= dt;
        if (this.stormT <= 0) {
          thunder(this.synth, t, 0.25 + 0.5 * i.prox, i.prox, rng.bi() * 0.5);
          this.stormT = rng.range(2.5, 6) * (1.5 - i.prox);
        }
      }
    } else {
      if (this.critterT <= 0) {
        if (!i.crashed) sandGust(this.synth, t, i.playing ? 0.12 : 0.16, rng.range(1.6, 2.6), rng.bi() * 0.8);
        this.critterT = rng.range(7, 14);
      }
      if (i.playing && i.prox > 0.35) {
        this.stormT -= dt;
        if (this.stormT <= 0) {
          sandGust(this.synth, t, 0.08 + 0.16 * i.prox, rng.range(1.2, 2), rng.bi() * 0.4);
          this.stormT = rng.range(2, 4.5) * (1.5 - i.prox);
        }
      }
      if (this.rareT <= 0) {
        if (!i.playing) singingDune(this.synth, t, 0.05, rng.pick([87.31, 92.5, 98]));
        this.rareT = rng.range(25, 45);
      }
    }
  }

  fadeOut(now: number, seconds: number): number {
    return this.route.fade(0, now, seconds);
  }

  dispose(): void {
    this.rig.dispose();
    this.route.dispose();
  }
}
